import React from "react";
import { Box } from "@mui/material";
import { StyledButton, TabTop, StaticInfoMessage } from "components/common";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance, DateHelper } from "utils/helpers";
import { DisplayType } from "models/enums/style";
import cssExports from "theme/styles/main.module.scss";

export interface ScheduledChangesInfo {
  hasScheduledChanges: boolean;
  effectiveDate?: Date;
  scheduledFields: string[];
}

interface ScheduledChangesProps {
  scheduledChanges: ScheduledChangesInfo;
  currentTab: "current" | "scheduled";
  onTabChange: (tab: "current" | "scheduled") => void;
  onCancelScheduledChanges: () => void;
}

const ScheduledChanges: React.FC<ScheduledChangesProps> = ({
  scheduledChanges,
  currentTab,
  onTabChange,
  onCancelScheduledChanges,
}) => {
  if (!scheduledChanges.hasScheduledChanges) {
    return null;
  }

  const formattedDate = scheduledChanges.effectiveDate ? DateHelper.formatDate(scheduledChanges.effectiveDate) : "";

  // Get the message with placeholder replaced, then add line break after the date
  const messageWithDate = LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Settings.ScheduledChangesDescription,
    "",
    { date: `<strong>${formattedDate}</strong>` }
  );

  // Insert <br> after the date (which ends with </strong>.)
  const message = messageWithDate.replace(`</strong>.`, `</strong>.<br>`);

  // Convert string tab value to numeric index for TabTop
  const tabIndex = currentTab === "current" ? 0 : 1;
  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    onTabChange(newIndex === 0 ? "current" : "scheduled");
  };

  return (
    <Box sx={{ mb: 3, width: "100%" }}>
      <StaticInfoMessage
        displayType={DisplayType.Info}
        icon={IconDefinitionConstants.WCalendarTime}
        header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.UpcomingChanges)}
        className={cssExports.scheduledChangesAlert}
        fullWidth
      >
        <span className={cssExports.scheduledChangesMessage} dangerouslySetInnerHTML={{ __html: message }} />
      </StaticInfoMessage>

      <Box mt={2}>
        <TabTop
          tabKey="scheduled-changes-tabs"
          value={tabIndex}
          onChange={handleTabChange}
          tabs={[
            { text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.CurrentVersion) },
            { text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.ScheduledVersion) },
          ]}
        />
      </Box>

      {currentTab === "current" && (
        <Box mt={2}>
          <Box mb={2} className={`${cssExports.formControlDescription} ${cssExports.scheduledChangesDescription}`}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.CurrentVersionLockedInfo)}
          </Box>
          <StyledButton displayType={DisplayType.Info} size="sm" onClick={onCancelScheduledChanges}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.StopScheduling)}
          </StyledButton>
        </Box>
      )}

      {currentTab === "scheduled" && (
        <Box mt={2}>
          <Box mb={2} className={`${cssExports.formControlDescription} ${cssExports.scheduledChangesDescription}`}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.ScheduledChangesEditInfo)}
          </Box>
          <StyledButton displayType={DisplayType.Info} size="sm" onClick={onCancelScheduledChanges}>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.CancelScheduledChanges)}
          </StyledButton>
        </Box>
      )}
    </Box>
  );
};

export default ScheduledChanges;
